import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Card, Row, Col, Typography, Flex, Button, Input, InputNumber, Form, Divider, Space, message, Spin, Switch, Tag, Modal, Alert, Popconfirm, Avatar } from "antd";
import { EditOutlined, CheckCircleOutlined, LockOutlined, KeyOutlined, SafetyCertificateOutlined, CopyOutlined, CheckOutlined, CameraOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { getCookie } from "../../utils/cookieUtils";
import { setUserInfo } from "../../redux/slices/sessionSlice";
import { setCompanyInfo } from "../../redux/slices/executorSlice";
import { getCompanyProfile, updateCompanyAddress, updateTeamSize, updateCompanyPassword, setupAdmin2FA, verifyAdmin2FASetup, disableAdmin2FA } from "../../services/organizationService";
import ProfilePhotoModal from "../../general/components/profile-photo-modal/ProfilePhotoModal";
import "../../general/components/landing-page/auth2fa.css";

const { Title, Text } = Typography;

/**
 * 6-Slot OTP Component for 2FA Verification (Module Scope)
 */
const ProfileOtpInput = ({ value, onChange, onComplete, autoFocus }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    onChange(val);
    if (val.length === 6 && onComplete) {
      onComplete(val);
    }
  };

  return (
    <label className="auth-2fa-otp-wrapper" htmlFor="profile-2fa-otp-input">
      <input id="profile-2fa-otp-input" ref={inputRef} type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={value} onChange={handleChange} className="auth-2fa-otp-hidden-input" autoFocus={autoFocus} />
      <div className="auth-2fa-otp-grid">
        {[0, 1, 2, 3, 4, 5].map((idx) => {
          const char = value[idx] || "";
          const isFilled = value.length > idx;
          const isActive = value.length === idx;
          let slotContent = "";
          if (char) {
            slotContent = char;
          } else if (isActive) {
            slotContent = <span className="auth-2fa-cursor">|</span>;
          }
          return (
            <div key={idx} className={`auth-2fa-otp-slot ${isFilled ? "filled" : ""} ${isActive ? "active" : ""}`}>
              {slotContent}
            </div>
          );
        })}
      </div>
    </label>
  );
};

const cleanCompanyName = (name) => {
  if (!name) return "";
  return String(name).trim().replace(/,\s*$/, "").trim();
};

function CompanyProfile({ onOpenForgotPwd }) {
  const dispatch = useDispatch();
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Company profile state
  const [profile, setProfile] = useState({
    companyName: user?.companyName || "",
    companyCode: effectiveCompanyCode,
    name: user?.name || "Administrator",
    email: user?.email || "",
    countryCode: user?.countryCode || "+91",
    mobile: user?.mobile || "",
    industry: user?.industry || "",
    teamSize: Number(user?.teamSize || 20),
    createdAt: user?.createdAt || new Date().toISOString(),
    companyAddress: user?.companyAddress || "Chennai, Tamil Nadu, India",
    profilePhoto: user?.profilePhoto || "",
    twoFactorEnabled: Boolean(user?.twoFactorEnabled),
  });

  // Profile photo modal state
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  // Inline editing states
  const [editingTeamSize, setEditingTeamSize] = useState(false);
  const [teamSizeInput, setTeamSizeInput] = useState(20);

  const [editingAddress, setEditingAddress] = useState(false);
  const [addressInput, setAddressInput] = useState("");

  // Password form state
  const [newPasswordVal, setNewPasswordVal] = useState("");
  const [changePwdLoading, setChangePwdLoading] = useState(false);

  // 2FA states
  const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);
  const [twoFactorSetupData, setTwoFactorSetupData] = useState({
    qrCode: "",
    secret: "",
    otpauthUrl: "",
  });
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorVerifying, setTwoFactorVerifying] = useState(false);
  const [twoFactorDisabling, setTwoFactorDisabling] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);

  // Load Company Profile from Middleware
  const loadProfile = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      const res = await getCompanyProfile(effectiveCompanyCode);
      if (res?.success && res.company) {
        const c = res.company;
        const currentUser = userRef.current;
        const updatedProfile = {
          companyName: c.companyName || currentUser?.companyName || "",
          companyCode: c.companyCode || effectiveCompanyCode,
          name: c.name || currentUser?.name || "Administrator",
          email: c.email || currentUser?.email || "",
          countryCode: c.countryCode || currentUser?.countryCode || "+91",
          mobile: c.mobile || currentUser?.mobile || "",
          industry: c.industry || currentUser?.industry || "",
          teamSize: Number(c.teamSize || currentUser?.teamSize || 20),
          companyAddress: c.companyAddress || currentUser?.companyAddress || "Chennai, Tamil Nadu, India",
          profilePhoto: c.profilePhoto || currentUser?.profilePhoto || "",
          createdAt: c.createdAt || currentUser?.createdAt || new Date().toISOString(),
          twoFactorEnabled: Boolean(c.twoFactorEnabled),
        };
        setProfile((prev) => ({ ...prev, ...updatedProfile }));
        setTeamSizeInput(Number(c.teamSize || 20));
        setAddressInput(c.companyAddress || "Chennai, Tamil Nadu, India");

        // Sync to Redux store
        dispatch(
          setUserInfo({
            ...currentUser,
            ...updatedProfile,
          }),
        );
        dispatch(
          setCompanyInfo({
            companyCode: updatedProfile.companyCode,
            companyName: updatedProfile.companyName,
            companyAddress: updatedProfile.companyAddress,
          }),
        );
      }
    } catch (err) {
      console.error("Failed to load company profile:", err);
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyCode, dispatch]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Password strength checks
  const pwdChecks = useMemo(() => {
    return {
      length: newPasswordVal.length >= 8,
      upper: /[A-Z]/.test(newPasswordVal),
      number: /\d/.test(newPasswordVal),
      symbol: /[^A-Za-z\d]/.test(newPasswordVal),
    };
  }, [newPasswordVal]);

  const isPwdStrong = pwdChecks.length && pwdChecks.upper && pwdChecks.number && pwdChecks.symbol;

  // Handle Team Size Save
  const handleSaveTeamSize = async () => {
    if (!teamSizeInput || teamSizeInput < 1) {
      message.error("Please enter a valid team size (minimum 1)");
      return;
    }
    try {
      const res = await updateTeamSize(effectiveCompanyCode, teamSizeInput);
      if (res?.success !== false) {
        setProfile((prev) => ({ ...prev, teamSize: teamSizeInput }));
        setEditingTeamSize(false);
        dispatch(setUserInfo({ ...userRef.current, teamSize: teamSizeInput }));
        message.success("Team size updated successfully!");
      } else {
        message.error(res?.message || "Failed to update team size.");
      }
    } catch (err) {
      console.error(err);
      message.error("Error updating team size.");
    }
  };

  // Handle Address Save
  const handleSaveAddress = async () => {
    try {
      const res = await updateCompanyAddress(effectiveCompanyCode, addressInput.trim());
      if (res?.success !== false) {
        setProfile((prev) => ({ ...prev, companyAddress: addressInput.trim() }));
        setEditingAddress(false);
        dispatch(setUserInfo({ ...userRef.current, companyAddress: addressInput.trim() }));
        dispatch(setCompanyInfo({ companyAddress: addressInput.trim() }));
        message.success("Company address updated successfully!");
      } else {
        message.error(res?.message || "Failed to update address.");
      }
    } catch (err) {
      console.error(err);
      message.error("Error updating address.");
    }
  };

  // Handle Password Submit
  const handleChangePassword = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error("New password and confirm password do not match!");
      return;
    }
    setChangePwdLoading(true);
    try {
      const res = await updateCompanyPassword(effectiveCompanyCode, values.currentPassword, values.newPassword);
      if (res?.success !== false) {
        form.resetFields();
        setNewPasswordVal("");
        message.success("Password updated successfully!");
      } else {
        message.error(res?.message || "Failed to update password.");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to update password.");
    } finally {
      setChangePwdLoading(false);
    }
  };

  // Handle 2FA Setup Initiation
  const handleStart2FASetup = async () => {
    setTwoFactorLoading(true);
    setTwoFactorError("");
    setTwoFactorCode("");
    try {
      const res = await setupAdmin2FA(effectiveCompanyCode);
      if (res?.success && res.qrCode) {
        setTwoFactorSetupData({
          qrCode: res.qrCode,
          secret: res.secret,
          otpauthUrl: res.otpauthUrl,
        });
        setTwoFactorModalOpen(true);
      } else {
        message.error(res?.message || "Failed to generate 2FA QR code.");
      }
    } catch (err) {
      console.error("2FA setup error:", err);
      message.error("Failed to generate 2FA QR code.");
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // Handle 2FA Verification & Activation
  const handleVerify2FASetup = async (codeToVerify) => {
    const code = (codeToVerify || twoFactorCode).trim();
    if (code.length !== 6) {
      setTwoFactorError("Please enter the 6-digit code from your Authenticator app.");
      return;
    }
    setTwoFactorError("");
    setTwoFactorVerifying(true);
    try {
      const res = await verifyAdmin2FASetup(effectiveCompanyCode, code);
      if (res?.success) {
        setProfile((prev) => ({ ...prev, twoFactorEnabled: true }));
        setTwoFactorModalOpen(false);
        setTwoFactorCode("");
        dispatch(setUserInfo({ ...userRef.current, twoFactorEnabled: true }));
        message.success("Two-Factor Authentication enabled successfully!");
      } else {
        setTwoFactorError(res?.message || "Invalid verification code. Please try again.");
      }
    } catch (err) {
      console.error("2FA verification error:", err);
      setTwoFactorError(err.message || "Failed to verify 2FA code.");
    } finally {
      setTwoFactorVerifying(false);
    }
  };

  // Handle 2FA Disable
  const handleDisable2FA = async () => {
    setTwoFactorDisabling(true);
    try {
      const res = await disableAdmin2FA(effectiveCompanyCode);
      if (res?.success) {
        setProfile((prev) => ({ ...prev, twoFactorEnabled: false }));
        dispatch(setUserInfo({ ...userRef.current, twoFactorEnabled: false }));
        message.success("Two-Factor Authentication disabled.");
      } else {
        message.error(res?.message || "Failed to disable 2FA.");
      }
    } catch (err) {
      console.error("2FA disable error:", err);
      message.error("Failed to disable 2FA.");
    } finally {
      setTwoFactorDisabling(false);
    }
  };

  const copySecretKey = () => {
    if (!twoFactorSetupData.secret) return;
    navigator.clipboard?.writeText(twoFactorSetupData.secret);
    setCopiedSecret(true);
    message.success("Secret key copied to clipboard!");
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  return (
    <Spin spinning={loading}>
      <Flex vertical gap={16}>
        {/* ── CARD 1: COMPANY INFORMATION ── */}
        <Card className="org-card" styles={{ body: { padding: 20 } }}>
          <Flex vertical gap={16}>
            {/* Profile Avatar Header */}
            <Flex align="center" gap={16} style={{ padding: "8px 0" }}>
              <div style={{ position: "relative", display: "inline-block", cursor: "pointer" }} onClick={() => setIsPhotoModalOpen(true)} title="Change Profile Photo">
                <Avatar
                  size={64}
                  src={profile.profilePhoto || undefined}
                  style={{
                    backgroundColor: "#020817",
                    fontSize: "1.3rem",
                    fontWeight: 700,
                    color: "#fff",
                    border: "2px solid #e2e8f0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    cursor: "pointer",
                  }}
                >
                  {cleanCompanyName(profile.companyName) ? cleanCompanyName(profile.companyName).slice(0, 2).toUpperCase() : "DV"}
                </Avatar>
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    backgroundColor: "#2459d3",
                    color: "#ffffff",
                    borderRadius: "50%",
                    width: 22,
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                    border: "2px solid #ffffff",
                    cursor: "pointer",
                  }}
                >
                  <CameraOutlined style={{ fontSize: 10, color: "#ffffff" }} />
                </div>
              </div>
              <Flex vertical gap={2}>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>{cleanCompanyName(profile.companyName)}</div>
                <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                  {profile.name} • {profile.industry}
                </Text>
              </Flex>
            </Flex>

            <Divider style={{ margin: "2px 0 6px 0" }} />

            <Row gutter={[16, 14]}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Company Name
                </Text>
                <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "0.85rem" }}>{cleanCompanyName(profile.companyName)}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Company Code
                </Text>
                <div style={{ fontWeight: 700, color: "#2459d3", fontSize: "0.85rem" }}>{profile.companyCode}</div>
              </Col>

              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Administrator
                </Text>
                <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "0.85rem" }}>{profile.name}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Contact Email
                </Text>
                <div style={{ fontWeight: 500, color: "#475569", fontSize: "0.85rem" }}>{profile.email}</div>
              </Col>

              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Phone Number
                </Text>
                <div style={{ fontWeight: 500, color: "#475569", fontSize: "0.85rem" }}>
                  {profile.countryCode} {profile.mobile}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Industry
                </Text>
                <div style={{ fontWeight: 500, color: "#475569", fontSize: "0.85rem" }}>{profile.industry}</div>
              </Col>

              {/* Team Size with Inline Edit */}
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Team Size
                </Text>
                {editingTeamSize ? (
                  <Flex align="center" gap={6} style={{ marginTop: 2 }}>
                    <InputNumber min={1} value={teamSizeInput} onChange={setTeamSizeInput} size="small" style={{ width: 70 }} />
                    <Button type="primary" size="small" onClick={handleSaveTeamSize}>
                      Save
                    </Button>
                    <Button size="small" onClick={() => setEditingTeamSize(false)}>
                      Cancel
                    </Button>
                  </Flex>
                ) : (
                  <Flex align="center" gap={6} style={{ marginTop: 2 }}>
                    <span style={{ fontWeight: 700, color: "#2459d3", fontSize: "0.9rem" }}>{profile.teamSize} seats</span>
                    <Button type="text" size="small" icon={<EditOutlined style={{ color: "#94a3b8" }} />} onClick={() => setEditingTeamSize(true)} />
                  </Flex>
                )}
              </Col>

              <Col span={12}>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Member Since
                </Text>
                <div style={{ fontWeight: 500, color: "#64748b", fontSize: "0.85rem" }}>
                  {new Date(profile.createdAt).toLocaleDateString("en-IN", {
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </Col>
            </Row>

            {/* Address with Inline Edit */}
            <Divider style={{ margin: "4px 0" }} />
            <div>
              <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                Company Address
              </Text>
              {editingAddress ? (
                <Flex vertical gap={6} style={{ marginTop: 4 }}>
                  <Input.TextArea rows={2} value={addressInput} onChange={(e) => setAddressInput(e.target.value)} size="small" />
                  <Flex gap={6}>
                    <Button type="primary" size="small" onClick={handleSaveAddress}>
                      Save Address
                    </Button>
                    <Button size="small" onClick={() => setEditingAddress(false)}>
                      Cancel
                    </Button>
                  </Flex>
                </Flex>
              ) : (
                <Flex justify="space-between" align="start" style={{ marginTop: 2 }}>
                  <Text style={{ fontSize: "0.82rem", color: "#334155", maxWidth: "88%" }}>{profile.companyAddress}</Text>
                  <Button type="text" size="small" icon={<EditOutlined style={{ color: "#94a3b8" }} />} onClick={() => setEditingAddress(true)} />
                </Flex>
              )}
            </div>
          </Flex>
        </Card>

        {/* ── CARD 2: CHANGE PASSWORD ── */}
        <Card className="org-card" styles={{ body: { padding: 20 } }}>
          <Flex vertical gap={14}>
            <Flex justify="space-between" align="center">
              <Flex align="center" gap={8}>
                <LockOutlined style={{ color: "#2459d3", fontSize: 16 }} />
                <Title level={5} style={{ margin: 0, color: "#1e293b", fontSize: "0.95rem" }}>
                  Change Password
                </Title>
              </Flex>
              {onOpenForgotPwd && (
                <Button type="link" size="small" icon={<KeyOutlined />} onClick={onOpenForgotPwd} style={{ fontSize: "0.8rem", padding: 0 }}>
                  Forgot Password?
                </Button>
              )}
            </Flex>

            <Form form={form} layout="vertical" onFinish={handleChangePassword} size="middle">
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label={<span style={{ fontSize: "0.8rem" }}>Current Password</span>} name="currentPassword" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 12 }}>
                    <Input.Password placeholder="••••••••" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<span style={{ fontSize: "0.8rem" }}>New Password</span>} name="newPassword" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 12 }}>
                    <Input.Password placeholder="••••••••" value={newPasswordVal} onChange={(e) => setNewPasswordVal(e.target.value)} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label={<span style={{ fontSize: "0.8rem" }}>Confirm Password</span>} name="confirmPassword" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 12 }}>
                    <Input.Password placeholder="••••••••" />
                  </Form.Item>
                </Col>

                {/* Password Strength Indicator */}
                <Col span={12}>
                  {newPasswordVal && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: "0.72rem", display: "block" }}>
                        Requirements:
                      </Text>
                      <Space direction="vertical" size={2} style={{ marginTop: 2 }}>
                        <Text style={{ fontSize: "0.72rem", color: pwdChecks.length ? "#10b981" : "#94a3b8" }}>
                          <CheckCircleOutlined /> Min 8 characters
                        </Text>
                        <Text style={{ fontSize: "0.72rem", color: pwdChecks.upper ? "#10b981" : "#94a3b8" }}>
                          <CheckCircleOutlined /> Uppercase letter
                        </Text>
                        <Text style={{ fontSize: "0.72rem", color: pwdChecks.number ? "#10b981" : "#94a3b8" }}>
                          <CheckCircleOutlined /> Number
                        </Text>
                        <Text style={{ fontSize: "0.72rem", color: pwdChecks.symbol ? "#10b981" : "#94a3b8" }}>
                          <CheckCircleOutlined /> Symbol (!@#$)
                        </Text>
                      </Space>
                    </div>
                  )}
                </Col>
              </Row>

              <Flex justify="end">
                <Button type="primary" htmlType="submit" loading={changePwdLoading} disabled={newPasswordVal.length > 0 && !isPwdStrong}>
                  Update Password
                </Button>
              </Flex>
            </Form>
          </Flex>
        </Card>

        {/* ── CARD 3: TWO-FACTOR AUTHENTICATION (2FA) ── */}
        <Card className="org-card" styles={{ body: { padding: 20 } }}>
          <Flex vertical gap={14}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
              <Flex align="center" gap={10}>
                <SafetyCertificateOutlined style={{ color: "#2459d3", fontSize: 18 }} />
                <div>
                  <Flex align="center" gap={8}>
                    <Title level={5} style={{ margin: 0, color: "#1e293b", fontSize: "0.95rem" }}>
                      Two-Factor Authentication (2FA)
                    </Title>
                    <Tag color={profile.twoFactorEnabled ? "success" : "default"} style={{ margin: 0, fontWeight: 600, fontSize: "0.72rem" }}>
                      {profile.twoFactorEnabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Flex>
                  <Text type="secondary" style={{ fontSize: "0.78rem", display: "block", marginTop: 2 }}>
                    Add an extra layer of security. When enabled, signing in requires a 6-digit TOTP code from your Authenticator app.
                  </Text>
                </div>
              </Flex>

              {profile.twoFactorEnabled ? (
                <Popconfirm title="Disable Two-Factor Authentication" description="Are you sure you want to disable 2FA? This will reduce account login security." onConfirm={handleDisable2FA} okText="Disable 2FA" cancelText="Cancel" okButtonProps={{ danger: true, loading: twoFactorDisabling }}>
                  <Switch checked={true} loading={twoFactorDisabling} checkedChildren="ON" unCheckedChildren="OFF" />
                </Popconfirm>
              ) : (
                <Switch checked={false} loading={twoFactorLoading} onChange={handleStart2FASetup} checkedChildren="ON" unCheckedChildren="OFF" />
              )}
            </Flex>
          </Flex>
        </Card>
      </Flex>

      {/* ── 2FA SETUP MODAL ── */}
      <Modal
        open={twoFactorModalOpen}
        onCancel={() => {
          if (!twoFactorVerifying) {
            setTwoFactorModalOpen(false);
            setTwoFactorCode("");
            setTwoFactorError("");
          }
        }}
        footer={null}
        width={420}
        centered
        styles={{ body: { padding: "24px 20px" } }}
      >
        <div className="auth-2fa-container" style={{ maxWidth: 360 }}>
          <div className="auth-2fa-kicker">Security Setup</div>
          <h2 className="auth-2fa-title">Setup Authenticator</h2>
          <p className="auth-2fa-description">
            Scan this QR code using the Authenticator app (such as <strong>AuthSoft</strong>, Google Authenticator, or Microsoft Authenticator) to activate 2FA for Admin login.
          </p>

          <a href="https://play.google.com/store/apps/details?id=com.softrate.authsoft&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="authsoft-download-pill">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
              <path d="M3.609 1.814L13.793 12 3.61 22.186A1.5 1.5 0 0 1 3 21.2V2.8a1.5 1.5 0 0 1 .609-.986zm11.3 11.3l2.296 2.295-9.845 5.684 7.549-7.979zm2.296-3.71l-2.296 2.296-7.55-7.98 9.846 5.684zm1.118.646l2.84 1.64a.8.8 0 0 1 0 1.388l-2.84 1.64-2.148-2.334 2.148-2.334z" />
            </svg>
            <span>Get AuthSoft on Google Play</span>
            <span style={{ fontSize: 10, opacity: 0.7 }}>↗</span>
          </a>

          <div className="auth-2fa-qr-card">
            <div className="auth-2fa-account-badge">
              <span>DealVoice</span>
              <span>•</span>
              <span className="highlight">{profile.companyName || profile.name || "Admin"}</span>
            </div>

            {twoFactorSetupData.qrCode && (
              <div className="auth-2fa-qr-wrapper">
                <img src={twoFactorSetupData.qrCode} alt="DealVoice TOTP QR Code" className="auth-2fa-qr-image" />
              </div>
            )}

            {twoFactorSetupData.secret && (
              <div className="auth-2fa-secret-box">
                <div className="auth-2fa-secret-label">Can't scan? Copy key:</div>
                <div className="auth-2fa-secret-row">
                  <span className="auth-2fa-secret-code">{twoFactorSetupData.secret}</span>
                  <button type="button" className="auth-2fa-copy-btn" onClick={copySecretKey}>
                    {copiedSecret ? <CheckOutlined style={{ color: "#16a34a" }} /> : <CopyOutlined />}
                    <span>{copiedSecret ? "Copied" : "Copy"}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="auth-2fa-otp-section">
            <span className="auth-2fa-otp-label">Enter 6-digit Authenticator Code</span>
            <ProfileOtpInput
              value={twoFactorCode}
              onChange={(val) => {
                setTwoFactorCode(val);
                if (twoFactorError) setTwoFactorError("");
              }}
              onComplete={handleVerify2FASetup}
              autoFocus
            />
          </div>

          {twoFactorError && <Alert message={twoFactorError} type="error" showIcon style={{ marginBottom: "12px", fontSize: "0.82rem" }} />}

          <Button type="primary" size="large" block className="auth-2fa-submit-btn" loading={twoFactorVerifying} disabled={twoFactorCode.length !== 6} onClick={() => handleVerify2FASetup(twoFactorCode)}>
            Verify & Activate
          </Button>

          <Button
            type="text"
            block
            style={{ marginTop: 8, color: "#64748b" }}
            onClick={() => {
              setTwoFactorModalOpen(false);
              setTwoFactorCode("");
              setTwoFactorError("");
            }}
          >
            Cancel
          </Button>
        </div>
      </Modal>

      {/* ── PROFILE PHOTO MODAL ── */}
      <ProfilePhotoModal
        open={isPhotoModalOpen}
        onCancel={() => setIsPhotoModalOpen(false)}
        currentPhoto={profile.profilePhoto}
        userName={profile.name}
        companyName={profile.companyName}
        role="company_admin"
        companyCode={effectiveCompanyCode}
        userInfo={user}
        onSuccess={(newPhoto) => {
          setProfile((prev) => ({ ...prev, profilePhoto: newPhoto }));
        }}
      />
    </Spin>
  );
}

export default CompanyProfile;
